// src/admin/components/kits/GenerateKitCodesModal.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import FormInput from '../../../shared/components/FormInput';
import FormSelect from '../../../shared/components/FormSelect';

export default function GenerateKitCodesModal({
  show,
  onClose,
  onGenerate,
  kits,
  loadingKits,
}) {
  const [count, setCount] = useState(10);
  const [name, setName] = useState('');
  const [kitId, setKitId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show) {
      setCount(10);
      setName('');
      setError(null);
      setIsSubmitting(false);
      setKitId('');
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await onGenerate(count, parseInt(kitId, 10), name);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to generate kit codes');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid =
    count >= 1 &&
    count <= 1024 &&
    kitId &&
    !Number.isNaN(parseInt(kitId, 10));

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button
        type="button"
        className="btn btn-link text-muted"
        onClick={onClose}
        disabled={isSubmitting}
      >
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button
        type="submit"
        form="generate-kit-codes-form"
        className="btn btn-primary"
        disabled={!isValid || isSubmitting}
      >
        {isSubmitting ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
            />
            Generating...
          </>
        ) : (
          <>
            <i className="bi bi-plus-lg me-1" />
            Generate kit codes
          </>
        )}
      </button>
    </div>
  );

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title="Generate kit codes"
      footer={footer}
      size=""
    >
      <form id="generate-kit-codes-form" onSubmit={handleSubmit}>
        <div className="alert alert-light border mb-4">
          <i className="bi bi-info-circle me-2" />
          <span>
            Generate a batch of single-use kit codes for a specific spare or
            upgrade kit. Codes can be printed as QR labels and shipped with
            the kit to prove genuine usage in workflows.
          </span>
        </div>

        {error && (
          <div className="alert alert-danger mb-3">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        <FormSelect
          id="kit-select"
          label="Kit"
          value={kitId}
          onChange={(e) => setKitId(e.target.value)}
          disabled={loadingKits}
          required
          helperText="Kit codes in this batch will all be tied to the selected kit."
        >
          <option value="">
            {loadingKits ? 'Loading kits…' : 'Select a kit'}
          </option>
          {kits.map((kit) => (
            <option key={kit.id} value={kit.id}>
              {kit.name} {kit.sku ? `(${kit.sku})` : ''}
            </option>
          ))}
        </FormSelect>

        <FormInput
          id="kit-code-count"
          type="number"
          label="Number of codes to generate"
          value={count}
          onChange={(e) => setCount(parseInt(e.target.value, 10) || 0)}
          helperText="Enter a number between 1 and 1024. Consider generating a small buffer for damaged or lost labels."
          min={1}
          max={1024}
          required
        />

        <FormInput
          id="kit-batch-name"
          label={<>Batch name <span className="text-muted">(optional)</span></>}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Pump seal kits - Q3 2026"
          helperText="A descriptive name to help identify this batch later (e.g., kit version, customer, or campaign)."
        />
      </form>
    </AdminModal>
  );
}

GenerateKitCodesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  kits: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadingKits: PropTypes.bool,
};

GenerateKitCodesModal.defaultProps = {
  loadingKits: false,
};

